import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine
} from 'recharts';
import {
  Thermometer,
  Waves,
  Droplets,
  FlaskConical,
  AlertTriangle,
  Activity,
  ArrowRight,
  BrainCircuit,
  type LucideIcon
} from 'lucide-react';
import type { ClinicalCase } from '@/app/utils/vision-biology';

interface VisionAlert {
  id: string;
  createdAt: string;
  metadata?: Record<string, any>;
}

type TrendRange = '24h' | '7d' | '30d';
type SensorSource = 'aql-sensor-readings' | 'none';
type MetricLevel = 'Normal' | 'Warning' | 'Critico';

interface SensorPoint {
  timestamp: string;
  location: string;
  temperature: number | null;
  ph: number | null;
  oxygen: number | null;
  salinity: number | null;
  redox: number | null;
}

type DistributionMetric = 'temperature' | 'oxygen' | 'ph' | 'salinity';

interface DistributionStats {
  values: number[];
  outliers: number[];
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  n: number;
}

interface DistributionRow {
  id: string;
  location: string;
  metric: DistributionMetric;
  label: string;
  unit: string;
  decimals: number;
  stats: DistributionStats;
}

interface VisionSensorsDashboardProps {
  alerts?: VisionAlert[];
  cases?: ClinicalCase[];
  onOpenAlerts?: () => void;
  apiUrl?: string;
  authToken?: string;
  publicAnonKey?: string;
  embedded?: boolean;
  demoMode?: boolean;
  deviceOnline?: boolean;
  scope?: {
    clientId?: string | null;
    projectId?: string | null;
    tankId?: string | null;
  };
  sensorReadings?: Array<{
    id?: string | null;
    clientId?: string | null;
    deviceId?: string | null;
    sensorType?: string | null;
    value?: number | null;
    unit?: string | null;
    timestamp?: string | null;
    site?: string | null;
    tank?: string | null;
    source?: string | null;
    status?: string | null;
  }> | null;
}

const METRIC_KEYS = {
  temperature: ['temperature', 'temp', 'waterTemp', 'waterTemperature', 'water_temp', 'tempC', 'temp_c', 'temperatureC', 'temperature_c', 'temperatura', 'Temperature', 'Temp'],
  ph: ['ph', 'pH'],
  oxygen: ['oxygen', 'o2', 'oxygenDissolved', 'oxigenio'],
  salinity: ['salinity', 'salt', 'salinidade'],
  redox: ['redox', 'orp', 'redox_mv', 'orp_mv', 'potencial_oxirreducao', 'potencialOxirreducao']
};

const SENSOR_NAME_ALIASES = {
  temperature: ['temperature', 'temp', 'temperatura', 'water temperature', 'water temp', 'temp c', 'temperature c'],
  ph: ['ph', 'p h'],
  oxygen: ['oxygen', 'o2', 'dissolved oxygen', 'oxygen dissolved', 'oxigenio'],
  salinity: ['salinity', 'salt', 'salinidade'],
  redox: ['redox', 'orp', 'potencial oxirreducao', 'potencial de oxirreducao']
} satisfies Record<keyof typeof METRIC_KEYS, string[]>;

const METRIC_TONE: Record<MetricLevel, string> = {
  Critico: 'border-red-200/80 bg-red-50/80 text-red-700 dark:border-red-500/35 dark:bg-red-500/12 dark:text-red-200',
  Warning: 'border-amber-200/80 bg-amber-50/80 text-amber-700 dark:border-amber-500/35 dark:bg-amber-500/12 dark:text-amber-200',
  Normal: 'border-emerald-200/80 bg-emerald-50/70 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200'
};

const METRIC_LABEL: Record<MetricLevel, string> = {
  Critico: 'Crítico',
  Warning: 'Atenção',
  Normal: 'OK'
};

const num = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace(',', '.').trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeMetricValue = (metric: keyof typeof METRIC_KEYS, value: number | null) => {
  if (value === null || !Number.isFinite(value)) return null;
  if (metric === 'ph') return value > 0 && value <= 14 ? value : null;
  if (metric === 'temperature' || metric === 'oxygen' || metric === 'salinity') return value > 0 ? value : null;
  if (metric === 'redox') return value === 0 ? null : value;
  return value;
};

const normalizeKey = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const metricFromUnit = (unit: unknown): keyof typeof METRIC_KEYS | null => {
  const normalized = normalizeKey(unit);
  if (!normalized) return null;
  if (normalized === 'c' || normalized === 'deg c' || normalized === '°c' || normalized.includes('celsius')) return 'temperature';
  if (normalized === 'ph') return 'ph';
  if (normalized.includes('mg/l') || normalized.includes('mg l')) return 'oxygen';
  if (normalized === 'mv' || normalized.includes('millivolt')) return 'redox';
  if (normalized === 'psu' || normalized === 'ppt') return 'salinity';
  return null;
};

const metricLevel = (metric: keyof Pick<SensorPoint, 'temperature' | 'ph' | 'oxygen' | 'salinity'>, value: number): MetricLevel => {
  if (metric === 'temperature') return value > 30 || value < 17 ? 'Critico' : value > 28 || value < 19 ? 'Warning' : 'Normal';
  if (metric === 'ph') return value > 8.4 || value < 6.7 ? 'Critico' : value > 8.1 || value < 6.9 ? 'Warning' : 'Normal';
  if (metric === 'oxygen') return value < 5 ? 'Critico' : value < 5.5 ? 'Warning' : 'Normal';
  return value > 37 || value < 28 ? 'Critico' : value > 36 || value < 30 ? 'Warning' : 'Normal';
};

const sourceMeta = (source: SensorSource) => {
  if (source === 'aql-sensor-readings') return { label: 'Leituras ambientais registadas', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/12 dark:text-emerald-200 dark:border-emerald-500/30' };
  return { label: 'Sem feed de sensores', tone: 'bg-muted/40 text-muted-foreground border-border' };
};

const format = (value: number | null, decimals = 1) => (value === null || Number.isNaN(value) ? '--' : value.toFixed(decimals));
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const DISTRIBUTION_METRICS = [
  { key: 'temperature', label: 'Temp', unit: '°C', decimals: 1, tone: 'text-orange-700 dark:text-orange-200', stroke: '#f97316', fill: 'rgba(249,115,22,0.18)' },
  { key: 'oxygen', label: 'O2', unit: 'mg/L', decimals: 1, tone: 'text-emerald-700 dark:text-emerald-200', stroke: '#10b981', fill: 'rgba(16,185,129,0.18)' },
  { key: 'ph', label: 'pH', unit: '', decimals: 2, tone: 'text-sky-700 dark:text-sky-200', stroke: '#0ea5e9', fill: 'rgba(14,165,233,0.16)' },
  { key: 'salinity', label: 'Salinidade', unit: 'PSU', decimals: 1, tone: 'text-blue-700 dark:text-blue-200', stroke: '#2563eb', fill: 'rgba(37,99,235,0.16)' }
] as const;

const quantile = (sortedValues: number[], q: number) => {
  if (!sortedValues.length) return 0;
  const position = (sortedValues.length - 1) * q;
  const base = Math.floor(position);
  const rest = position - base;
  const next = sortedValues[base + 1];
  return next === undefined ? sortedValues[base] : sortedValues[base] + rest * (next - sortedValues[base]);
};

const buildDistributionStats = (values: number[]): DistributionStats | null => {
  const sorted = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const q1 = quantile(sorted, 0.25);
  const median = quantile(sorted, 0.5);
  const q3 = quantile(sorted, 0.75);
  const iqr = q3 - q1;
  const lowerFence = q1 - iqr * 1.5;
  const upperFence = q3 + iqr * 1.5;
  const inliers = sorted.filter((value) => value >= lowerFence && value <= upperFence);
  return {
    values: sorted,
    outliers: sorted.filter((value) => value < lowerFence || value > upperFence),
    min: inliers[0] ?? sorted[0],
    q1,
    median,
    q3,
    max: inliers[inliers.length - 1] ?? sorted[sorted.length - 1],
    n: sorted.length
  };
};

function DistributionBoxPlot({
  row,
  domain
}: {
  row: DistributionRow;
  domain: { min: number; max: number };
}) {
  const metric = DISTRIBUTION_METRICS.find((item) => item.key === row.metric) ?? DISTRIBUTION_METRICS[0];
  const range = domain.max - domain.min || 1;
  const x = (value: number) => clamp(((value - domain.min) / range) * 100, 1, 99);
  const boxStart = x(row.stats.q1);
  const boxEnd = x(row.stats.q3);
  const boxWidth = Math.max(1, boxEnd - boxStart);
  const sampleStep = Math.max(1, Math.ceil(row.stats.values.length / 36));
  const visibleValues = row.stats.values.filter((_, index) => index % sampleStep === 0).slice(0, 36);

  return (
    <div className="grid grid-cols-[minmax(6.5rem,8rem)_minmax(4.25rem,5rem)_minmax(13rem,1fr)_minmax(6.5rem,7.5rem)] items-center gap-3 rounded-lg border border-border bg-card/80 px-3 py-2 text-xs">
      <div className="min-w-0">
        <p className="truncate font-semibold text-foreground">{row.location}</p>
        <p className="text-[10px] text-muted-foreground">n={row.stats.n}</p>
      </div>
      <div className={`font-black ${metric.tone}`}>{row.label}</div>
      <div className="relative h-10" role="img" aria-label={`${row.label}: mediana ${format(row.stats.median, row.decimals)} ${row.unit}`}>
        <span className="absolute top-1/2 h-px -translate-y-1/2 rounded-full" style={{ left: `${x(row.stats.min)}%`, width: `${Math.max(0.5, x(row.stats.max) - x(row.stats.min))}%`, backgroundColor: metric.stroke, opacity: 0.58 }} />
        <span className="absolute top-1/2 h-3 w-px -translate-y-1/2" style={{ left: `${x(row.stats.min)}%`, backgroundColor: metric.stroke, opacity: 0.64 }} />
        <span className="absolute top-1/2 h-3 w-px -translate-y-1/2" style={{ left: `${x(row.stats.max)}%`, backgroundColor: metric.stroke, opacity: 0.64 }} />
        {visibleValues.map((value, index) => (
          <span
            key={`${value}-${index}`}
            className="absolute h-1.5 w-1.5 rounded-full"
            style={{
              left: `${x(value)}%`,
              top: `${8 + (index % 4) * 5}px`,
              backgroundColor: metric.stroke,
              opacity: 0.23,
              transform: 'translateX(-50%)'
            }}
          />
        ))}
        <span
          className="absolute top-1/2 h-4 -translate-y-1/2 rounded border"
          style={{
            left: `${boxStart}%`,
            width: `${boxWidth}%`,
            minWidth: '4px',
            backgroundColor: metric.fill,
            borderColor: metric.stroke
          }}
        />
        <span className="absolute top-1/2 h-6 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ left: `${x(row.stats.median)}%`, backgroundColor: metric.stroke }} />
        {row.stats.outliers.slice(0, 12).map((value, index) => (
          <span
            key={`outlier-${value}-${index}`}
            className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border bg-card"
            style={{ left: `${x(value)}%`, borderColor: metric.stroke }}
          />
        ))}
      </div>
      <div className="text-right">
        <p className="font-semibold text-foreground">{format(row.stats.median, row.decimals)}{row.unit ? ` ${row.unit}` : ''}</p>
        <p className="text-[10px] text-muted-foreground">mediana</p>
      </div>
    </div>
  );
}

const latest = (series: SensorPoint[], key: keyof Pick<SensorPoint, 'temperature' | 'ph' | 'oxygen' | 'salinity' | 'redox'>): number | null => {
  for (let i = series.length - 1; i >= 0; i -= 1) {
    const value = series[i][key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

interface SensorStatus {
  state: 'sem_leitura' | 'desatualizado' | 'normal' | 'atencao' | 'critico';
  label: string;
}

const classifySensor = (
  value: number | null,
  idealMin: number,
  idealMax: number,
  timestamp: string | null,
  maxAgeMinutes = 45
): SensorStatus => {
  if (value === null || Number.isNaN(value)) {
    return { state: 'sem_leitura', label: 'Sem leitura válida' };
  }

  if (timestamp) {
    const now = Date.now();
    const age = Math.round((now - new Date(timestamp).getTime()) / 60000);
    if (age > maxAgeMinutes) {
      return { state: 'desatualizado', label: 'Desatualizado' };
    }
  }

  if (value < idealMin || value > idealMax) {
    const distFromMin = Math.abs(value - idealMin);
    const distFromMax = Math.abs(value - idealMax);
    const threshold = (idealMax - idealMin) * 0.15;
    if (distFromMin < threshold || distFromMax < threshold) {
      return { state: 'atencao', label: 'Atenção' };
    }
    return { state: 'critico', label: 'Crítico' };
  }

  return { state: 'normal', label: 'Normal' };
};

interface WaterQualityIndexResult {
  status: 'insufficient_data' | 'partial' | 'complete';
  label: string;
  score: number | null;
  coverage: string;
  availableCount: number;
  requiredCount: number;
}

const calculateWaterQualityIndex = (
  temperature: number | null,
  ph: number | null,
  oxygen: number | null,
  salinity: number | null
): WaterQualityIndexResult => {
  const sensors = { temperature, ph, oxygen, salinity };
  const available = Object.values(sensors).filter((v) => v !== null && !Number.isNaN(v)).length;
  const total = 4;

  if (available < 3) {
    return {
      status: 'insufficient_data',
      label: 'Dados insuficientes',
      score: null,
      coverage: `${available}/${total} sensores com leitura`,
      availableCount: available,
      requiredCount: 3
    };
  }

  if (available === 3) {
    return {
      status: 'partial',
      label: 'Avaliação parcial',
      score: null,
      coverage: `${available}/${total} sensores com leitura`,
      availableCount: available,
      requiredCount: 3
    };
  }

  // Calculate composite score (0-100) when all sensors available
  let score = 100;
  if (temperature !== null) {
    const tempDev = Math.abs(temperature - 22) / 13; // ideal ~22, max dev ~13
    score -= Math.min(30, tempDev * 30);
  }
  if (ph !== null) {
    const phDev = Math.abs(ph - 7.5) / 1.4; // ideal ~7.5, max dev ~1.4
    score -= Math.min(25, phDev * 25);
  }
  if (oxygen !== null) {
    const o2Dev = Math.max(0, (6 - oxygen) / 1); // ideal ≥6, penalty for lower
    score -= Math.min(25, o2Dev * 25);
  }
  if (salinity !== null) {
    const salDev = Math.abs(salinity - 32.5) / 9; // ideal ~32.5, range 28-37
    score -= Math.min(20, salDev * 20);
  }

  score = Math.max(0, Math.round(score));

  return {
    status: 'complete',
    label: score >= 80 ? 'Estável' : score >= 60 ? 'Atenção' : 'Crítico',
    score,
    coverage: `${available}/${total} sensores com leitura`,
    availableCount: available,
    requiredCount: 3
  };
};

const rangeLabel = (timestamp: string, range: TrendRange) => {
  const date = new Date(timestamp);
  return range === '24h'
    ? date.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
};

const normalizeSensorReadingRows = (rows: NonNullable<VisionSensorsDashboardProps['sensorReadings']>): SensorPoint[] =>
  rows
    .map((row): SensorPoint | null => {
      const timestamp = row.timestamp ?? '';
      if (!Number.isFinite(new Date(timestamp).getTime())) return null;
      const sensorType = normalizeKey(row.sensorType);
      const metric = metricFromUnit(row.unit) ?? (sensorType.includes('temp') || sensorType.includes('temperatura')
        ? 'temperature'
        : sensorType === 'ph' || sensorType.includes('p h')
          ? 'ph'
          : sensorType.includes('oxygen') || sensorType.includes('oxigen') || sensorType.includes('o2')
            ? 'oxygen'
            : sensorType.includes('salinity') || sensorType.includes('salinidade') || sensorType.includes('salt')
              ? 'salinity'
              : sensorType.includes('redox') || sensorType.includes('orp')
                ? 'redox'
                : null);
      const rawRow = row as typeof row & Record<string, unknown>;
      const value = metric ? normalizeMetricValue(metric, num(row.value ?? rawRow.readingValue ?? rawRow.reading_value ?? rawRow.lastValue ?? rawRow.last_value ?? rawRow.reading)) : null;
      if (value === null) return null;
      return {
        timestamp,
        location: row.tank ?? row.site ?? row.deviceId ?? 'Leitura ambiental',
        temperature: metric === 'temperature' ? value : null,
        ph: metric === 'ph' ? value : null,
        oxygen: metric === 'oxygen' ? value : null,
        salinity: metric === 'salinity' ? value : null,
        redox: metric === 'redox' ? value : null
      };
    })
    .filter((row): row is SensorPoint => Boolean(row))
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

const mergeReadingRows = (
  primary: VisionSensorsDashboardProps['sensorReadings'],
  fallback: VisionSensorsDashboardProps['sensorReadings']
): NonNullable<VisionSensorsDashboardProps['sensorReadings']> =>
  Array.from(
    new Map(
      [...(primary ?? []), ...(fallback ?? [])]
        .sort((a, b) => new Date(b.timestamp ?? 0).getTime() - new Date(a.timestamp ?? 0).getTime())
        .map((row, index) => [row.id || `${row.sensorType ?? 'sensor'}-${row.timestamp ?? index}-${index}`, row])
    ).values()
  );

export function VisionSensorsDashboard({ cases = [], onOpenAlerts = () => {}, sensorReadings = null, scope }: VisionSensorsDashboardProps) {
  const [trendRange, setTrendRange] = useState<TrendRange>('24h');
  const [sensorSeries, setSensorSeries] = useState<SensorPoint[]>([]);
  const [sensorSource, setSensorSource] = useState<SensorSource>('none');
  const [loadingSource, setLoadingSource] = useState(true);
  const [directSensorReadings, setDirectSensorReadings] = useState<VisionSensorsDashboardProps['sensorReadings']>(sensorReadings);
  const mergedSensorReadings = useMemo(() => mergeReadingRows(directSensorReadings, sensorReadings), [directSensorReadings, sensorReadings]);
  const tableSensorSeries = useMemo(() => normalizeSensorReadingRows(mergedSensorReadings), [mergedSensorReadings]);

  useEffect(() => {
    setDirectSensorReadings(sensorReadings);
  }, [scope?.clientId, scope?.projectId, scope?.tankId, sensorReadings]);

  useEffect(() => {
    if (tableSensorSeries.length) {
      setSensorSeries(tableSensorSeries);
      setSensorSource('aql-sensor-readings');
      setLoadingSource(false);
      return;
    }

    setSensorSeries([]);
    setSensorSource('none');
    setLoadingSource(false);
  }, [tableSensorSeries]);

  const snapshot = useMemo(() => {
    const temperature = latest(sensorSeries, 'temperature');
    const ph = latest(sensorSeries, 'ph');
    const oxygen = latest(sensorSeries, 'oxygen');
    const salinity = latest(sensorSeries, 'salinity');
    const redox = latest(sensorSeries, 'redox');
    
    const lastTimestamp = sensorSeries.length > 0 ? sensorSeries[sensorSeries.length - 1].timestamp : null;
    
    const levels = {
      temperature: temperature === null ? null : metricLevel('temperature', temperature),
      ph: ph === null ? null : metricLevel('ph', ph),
      oxygen: oxygen === null ? null : metricLevel('oxygen', oxygen),
      salinity: salinity === null ? null : metricLevel('salinity', salinity)
    };
    
    const sensorStates = {
      temperature: classifySensor(temperature, 18, 22, lastTimestamp),
      ph: classifySensor(ph, 7, 8.2, lastTimestamp),
      oxygen: classifySensor(oxygen, 6, 9, lastTimestamp),
      salinity: classifySensor(salinity, 28, 37, lastTimestamp),
      redox: classifySensor(redox, 250, 400, lastTimestamp)
    };
    
    const waterQuality = calculateWaterQualityIndex(temperature, ph, oxygen, salinity);
    
    const availableLevels = Object.values(levels).filter((level): level is MetricLevel => level !== null);
    const criticalCount = availableLevels.filter((l) => l === 'Critico').length;
    const warningCount = availableLevels.filter((l) => l === 'Warning').length;
    const stateLabel = !availableLevels.length ? 'Sem feed' : criticalCount >= 2 ? 'Crítico' : criticalCount === 1 || warningCount >= 2 ? 'Requer atenção' : 'Estável';
    const stateTone =
      stateLabel === 'Sem feed'
        ? 'text-muted-foreground bg-muted/40 border-border'
        : stateLabel === 'Crítico'
        ? 'text-red-700 bg-red-50/85 border-red-200 dark:text-red-200 dark:bg-red-500/12 dark:border-red-500/35'
        : stateLabel === 'Requer atenção'
          ? 'text-amber-700 bg-amber-50/85 border-amber-200 dark:text-amber-200 dark:bg-amber-500/12 dark:border-amber-500/35'
          : 'text-emerald-700 bg-emerald-50/85 border-emerald-200 dark:text-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30';
    
    return { 
      temperature, ph, oxygen, salinity, redox,
      levels, 
      sensorStates,
      waterQuality,
      criticalCount, 
      warningCount, 
      stateLabel, 
      stateTone,
      lastTimestamp 
    };
  }, [sensorSeries]);

  const latestTimestampLabel = useMemo(() => {
    if (!sensorSeries.length) return 'Sem atualização recente';
    return new Date(sensorSeries[sensorSeries.length - 1].timestamp).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, [sensorSeries]);

  const freshness = useMemo(() => {
    if (!sensorSeries.length) {
      return {
        label: 'Sem leituras recentes',
        compact: 'Sem feed',
        tone: 'border-border bg-muted/40 text-muted-foreground'
      };
    }
    const now = Date.now();
    const last = new Date(sensorSeries[sensorSeries.length - 1].timestamp).getTime();
    const minutes = Math.max(0, Math.round((now - last) / 60000));
    if (minutes <= 8) return { label: `Atualizado há ${minutes} min`, compact: 'Atualização em dia', tone: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200' };
    if (minutes <= 45) return { label: `Atualizado há ${minutes} min`, compact: 'Atualização recente', tone: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200' };
    if (minutes <= 120) return { label: `Atualizado há ${minutes} min`, compact: 'Atualização com atraso', tone: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200' };
    return { label: `Atualizado há ${Math.round(minutes / 60)} h`, compact: 'Atualização atrasada', tone: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/35 dark:bg-red-500/12 dark:text-red-200' };
  }, [sensorSeries]);

  const stateNote =
    snapshot.stateLabel === 'Sem feed'
      ? 'Ainda não há leituras ambientais com timestamp válido para construir a série temporal.'
      : snapshot.stateLabel === 'Crítico'
      ? 'Thresholds críticos detetados. Priorizar intervenção operacional imediata.'
      : snapshot.stateLabel === 'Requer atenção'
        ? 'Parâmetros com desvio moderado. Reforçar monitorização das leituras.'
        : 'Parâmetros estáveis dentro da faixa operacional esperada.';

  const trendData = useMemo(() => {
    if (!sensorSeries.length) return [];
    const now = Date.now();
    const rangeMs = { '24h': 86400000, '7d': 604800000, '30d': 2592000000 };
    const cutoff = now - rangeMs[trendRange];
    const filtered = sensorSeries.filter((row) => new Date(row.timestamp).getTime() >= cutoff);
    const source = filtered.length ? filtered : sensorSeries.slice(Math.max(sensorSeries.length - 40, 0));
    return source.map((row) => ({
      label: rangeLabel(row.timestamp, trendRange),
      timestamp: row.timestamp,
      temperature: row.temperature,
      ph: row.ph,
      oxygen: row.oxygen,
      salinity: row.salinity,
      redox: row.redox
    }));
  }, [sensorSeries, trendRange]);

  const trendOverview = useMemo(() => {
    const tempValues = trendData.map((row) => row.temperature).filter((value): value is number => typeof value === 'number');
    const oxygenValues = trendData.map((row) => row.oxygen).filter((value): value is number => typeof value === 'number');
    const phValues = trendData.map((row) => row.ph).filter((value): value is number => typeof value === 'number');
    let outOfBand = 0;
    trendData.forEach((row) => {
      if (typeof row.temperature === 'number' && metricLevel('temperature', row.temperature) !== 'Normal') outOfBand += 1;
      if (typeof row.oxygen === 'number' && metricLevel('oxygen', row.oxygen) !== 'Normal') outOfBand += 1;
      if (typeof row.ph === 'number' && metricLevel('ph', row.ph) !== 'Normal') outOfBand += 1;
    });
    return {
      tempAvg: tempValues.length ? tempValues.reduce((sum, value) => sum + value, 0) / tempValues.length : null,
      oxygenAvg: oxygenValues.length ? oxygenValues.reduce((sum, value) => sum + value, 0) / oxygenValues.length : null,
      phAvg: phValues.length ? phValues.reduce((sum, value) => sum + value, 0) / phValues.length : null,
      outOfBand
    };
  }, [trendData]);

  const peakTemp = useMemo(() => {
    let index = -1;
    let value = Number.NEGATIVE_INFINITY;
    trendData.forEach((row, i) => {
      if (typeof row.temperature === 'number' && row.temperature > value) {
        value = row.temperature;
        index = i;
      }
    });
    return index >= 0 ? { index, value } : null;
  }, [trendData]);

  const distributionRows = useMemo(() => {
    const byLocation = new Map<string, Record<DistributionMetric, number[]>>();
    sensorSeries.forEach((row) => {
      const bucket = byLocation.get(row.location) ?? {
        temperature: [],
        oxygen: [],
        ph: [],
        salinity: []
      };
      DISTRIBUTION_METRICS.forEach((metric) => {
        const value = row[metric.key];
        if (typeof value === 'number' && Number.isFinite(value)) {
          bucket[metric.key].push(value);
        }
      });
      byLocation.set(row.location, bucket);
    });

    return Array.from(byLocation.entries())
      .flatMap(([location, valuesByMetric]) =>
        DISTRIBUTION_METRICS.map((metric): DistributionRow | null => {
          const stats = buildDistributionStats(valuesByMetric[metric.key]);
          if (!stats) return null;
          return {
            id: `${location}-${metric.key}`,
            location,
            metric: metric.key,
            label: metric.label,
            unit: metric.unit,
            decimals: metric.decimals,
            stats
          };
        }).filter((row): row is DistributionRow => Boolean(row))
      )
      .sort((a, b) => {
        const locationDelta = a.location.localeCompare(b.location);
        if (locationDelta !== 0) return locationDelta;
        return DISTRIBUTION_METRICS.findIndex((metric) => metric.key === a.metric) -
          DISTRIBUTION_METRICS.findIndex((metric) => metric.key === b.metric);
      })
      .slice(0, 16);
  }, [sensorSeries]);

  const distributionDomains = useMemo(() => {
    const domains = new Map<DistributionMetric, { min: number; max: number }>();
    DISTRIBUTION_METRICS.forEach((metric) => {
      const values = distributionRows
        .filter((row) => row.metric === metric.key)
        .flatMap((row) => row.stats.values);
      if (!values.length) return;
      const min = Math.min(...values);
      const max = Math.max(...values);
      const padding = Math.max((max - min) * 0.08, metric.key === 'ph' ? 0.05 : 0.2);
      domains.set(metric.key, { min: min - padding, max: max + padding });
    });
    return domains;
  }, [distributionRows]);
  const visibleDistributionMetrics = DISTRIBUTION_METRICS.filter((metric) =>
    distributionRows.some((row) => row.metric === metric.key)
  );

  const thresholds = [
    { metric: 'Temperatura', value: `${format(snapshot.temperature, 1)} °C`, threshold: '18–22 °C', level: snapshot.levels.temperature, hint: 'Afeta metabolismo, consumo de oxigénio, alimentação e stress.' },
    { metric: 'pH', value: snapshot.ph !== null ? format(snapshot.ph, 2) : '--', threshold: '7–8.2 pH', level: snapshot.levels.ph, hint: 'Indica acidez/alcalinidade e influencia toxicidade e conforto fisiológico.' },
    { metric: 'Oxigénio Dissolvido', value: snapshot.oxygen !== null ? `${format(snapshot.oxygen, 1)} mg/L` : '--', threshold: '6+ mg/L', level: snapshot.levels.oxygen, hint: 'Parâmetro crítico para respiração, stress e mortalidade.' },
    { metric: 'Salinidade', value: snapshot.salinity !== null ? `${format(snapshot.salinity, 1)} PSU` : '--', threshold: '28–37 PSU', level: snapshot.levels.salinity, hint: 'Verificar renovação de água e mistura.' },
    { metric: 'Potencial de Oxirredução / ORP', value: snapshot.redox !== null ? `${format(snapshot.redox, 0)} mV` : '--', threshold: '250–400 mV', level: null, hint: 'Ajuda a interpretar equilíbrio oxidante/reduzente e condições químicas/microbiológicas.' }
  ] as Array<{ metric: string; value: string; threshold: string; level: MetricLevel | null; hint: string }>;

  const thresholdSummary = {
    critical: thresholds.slice(0, 4).filter((i) => i.level === 'Critico').length,
    warning: thresholds.slice(0, 4).filter((i) => i.level === 'Warning').length,
    normal: thresholds.slice(0, 4).filter((i) => i.level === 'Normal').length
  };

  const cards = [
    {
      id: 'temperature',
      title: 'Temperatura',
      value: snapshot.temperature,
      d: 1,
      unit: '°C',
      target: 'Ideal: 18–22 °C',
      text: 'Afeta metabolismo, consumo de oxigénio, alimentação e stress.',
      Icon: Thermometer,
      tone: 'border-orange-200/75 bg-gradient-to-br from-orange-50/60 via-card to-card dark:border-orange-500/25 dark:from-orange-500/10 dark:via-card dark:to-card',
      valueTone: 'text-orange-700 dark:text-orange-300',
      iconTone: 'border-orange-200/70 bg-orange-50/80 text-orange-700 dark:border-orange-500/25 dark:bg-orange-500/10 dark:text-orange-300',
      level: snapshot.levels.temperature,
      state: snapshot.sensorStates.temperature
    },
    {
      id: 'ph',
      title: 'pH',
      value: snapshot.ph,
      d: 2,
      unit: '',
      target: 'Ideal: 7–8.2 pH',
      text: 'Indica acidez/alcalinidade e influencia toxicidade e conforto fisiológico.',
      Icon: FlaskConical,
      tone: 'border-sky-200/75 bg-gradient-to-br from-sky-50/60 via-card to-card dark:border-sky-500/25 dark:from-sky-500/10 dark:via-card dark:to-card',
      valueTone: 'text-sky-700 dark:text-sky-300',
      iconTone: 'border-sky-200/70 bg-sky-50/80 text-sky-700 dark:border-sky-500/25 dark:bg-sky-500/10 dark:text-sky-300',
      level: snapshot.levels.ph,
      state: snapshot.sensorStates.ph
    },
    {
      id: 'oxygen',
      title: 'Oxigénio Dissolvido',
      value: snapshot.oxygen,
      d: 1,
      unit: 'mg/L',
      target: 'Mínimo: 6+ mg/L',
      text: 'Parâmetro crítico para respiração, stress e mortalidade.',
      Icon: Droplets,
      tone: 'border-emerald-200/75 bg-gradient-to-br from-emerald-50/60 via-card to-card dark:border-emerald-500/25 dark:from-emerald-500/10 dark:via-card dark:to-card',
      valueTone: 'text-emerald-700 dark:text-emerald-300',
      iconTone: 'border-emerald-200/70 bg-emerald-50/80 text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-300',
      level: snapshot.levels.oxygen,
      state: snapshot.sensorStates.oxygen
    },
    {
      id: 'salinity',
      title: 'Salinidade',
      value: snapshot.salinity,
      d: 1,
      unit: 'PSU',
      target: 'Ideal: 28–37 PSU',
      text: 'Verificar renovação de água e mistura.',
      Icon: Waves,
      tone: 'border-blue-200/75 bg-gradient-to-br from-blue-50/60 via-card to-card dark:border-blue-500/25 dark:from-blue-500/10 dark:via-card dark:to-card',
      valueTone: 'text-blue-700 dark:text-blue-300',
      iconTone: 'border-blue-200/70 bg-blue-50/80 text-blue-700 dark:border-blue-500/25 dark:bg-blue-500/10 dark:text-blue-300',
      level: snapshot.levels.salinity,
      state: snapshot.sensorStates.salinity
    }
  ] as Array<{
    id: string;
    title: string;
    value: number | null;
    d: number;
    unit: string;
    target: string;
    text: string;
    Icon: LucideIcon;
    tone: string;
    valueTone: string;
    iconTone: string;
    level: MetricLevel | null;
    state: SensorStatus;
  }>;

  const sourceInfo = sourceMeta(sensorSource);
  const lowOxygenObservationCount = cases.filter((item) => item.oxygen !== null && item.oxygen < 5.5).length;
  const juvenileCaseCount = cases.filter((item) => item.phase === 'juvenile').length;
  const biologicalSignals = [
    {
      title: 'O2 low -> higher clinical risk',
      value: `${lowOxygenObservationCount} tank observations`,
      description: 'Prioritize these tanks for validation before assuming a cause.',
      hasData: lowOxygenObservationCount > 0
    },
    {
      title: 'Dominant biological phase',
      value: `${juvenileCaseCount} juvenile cases in scope`,
      description: 'Phase concentration helps explain where environmental deviations may matter operationally.',
      hasData: cases.length > 0 && juvenileCaseCount > 0
    }
  ].filter((item) => item.hasData);
  const visibleSensorCards = cards;
  const hasSensorSummaryData = snapshot.waterQuality.status !== 'insufficient_data' || snapshot.lastTimestamp || sensorSource !== 'none' || loadingSource;
  const hasSensorDashboardData = biologicalSignals.length > 0 || visibleSensorCards.length > 0 || trendData.length > 0 || distributionRows.length > 0 || hasSensorSummaryData;
  const chartTone = {
    grid: 'var(--border)',
    axis: 'var(--muted-foreground)',
    cursor: 'var(--muted-foreground)',
    temp: '#f97316',
    ph: '#38bdf8',
    oxygen: '#22c55e',
    salinity: '#3b82f6'
  };
  const temperatureDot = (props: any) => {
    if (typeof props.value !== 'number') return null;
    return peakTemp && props.index === peakTemp.index
      ? <circle cx={props.cx} cy={props.cy} r={4.5} fill={chartTone.temp} stroke="var(--card)" strokeWidth={2} />
      : <circle cx={props.cx} cy={props.cy} r={2.2} fill={chartTone.temp} stroke="var(--card)" strokeWidth={1} opacity={0.72} />;
  };
  const rawPointDot = (color: string) => (props: any) => (
    typeof props.value === 'number'
      ? <circle cx={props.cx} cy={props.cy} r={2} fill={color} stroke="var(--card)" strokeWidth={0.8} opacity={0.62} />
      : null
  );

  const levelTone = (level: MetricLevel | null) => level ? METRIC_TONE[level] : 'border-border bg-muted/40 text-muted-foreground';
  const levelLabel = (level: MetricLevel | null) => level ? METRIC_LABEL[level] : 'Sem leitura';

  if (!loadingSource && !hasSensorDashboardData) {
    return null;
  }

  return (
    <div className="animate-in fade-in space-y-3 duration-500">
      {biologicalSignals.length > 0 ? (
        <div className="grid gap-3 lg:grid-cols-2">
          {biologicalSignals.map((item) => (
            <div key={item.title} className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="rounded-xl border border-border bg-muted/35 p-2">
                  <BrainCircuit className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{item.title}</p>
                  <p className="mt-1 font-semibold text-foreground">{item.value}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {hasSensorSummaryData ? (
        <div className="rounded-xl border border-border bg-gradient-to-br from-card via-card to-muted/35 p-3 shadow-[0_10px_24px_-18px_rgba(2,8,20,0.9)] md:p-3.5 dark:to-[#172338]/70">
        <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold tracking-tight text-foreground md:text-lg">Monitorização de Sensores</h2>
          <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${loadingSource ? 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200' : freshness.tone}`}>{loadingSource ? 'Sincronização em curso' : freshness.label}</span>
        </div>
        
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-4">
          {/* Qualidade da água */}
          {snapshot.waterQuality.status !== 'insufficient_data' ? (
            <div className={`min-h-20 rounded-lg border px-3 py-2.5 ${snapshot.waterQuality.label === 'Crítico' ? 'border-red-200/80 bg-red-50/50 dark:border-red-500/30 dark:bg-red-500/10' : snapshot.waterQuality.label === 'Atenção' ? 'border-amber-200/80 bg-amber-50/45 dark:border-amber-500/30 dark:bg-amber-500/10' : 'border-emerald-200/80 bg-emerald-50/45 dark:border-emerald-500/25 dark:bg-emerald-500/10'}`}>
              <p className="text-[10px] font-semibold uppercase leading-tight tracking-[0.08em] text-foreground/70">Qualidade da água</p>
              <p className="mt-1.5 text-sm font-semibold text-foreground">{snapshot.waterQuality.label}</p>
              {snapshot.waterQuality.score !== null ? (
                <p className="mt-1 text-[13px] font-bold text-foreground">{snapshot.waterQuality.score}/100</p>
              ) : null}
            </div>
          ) : null}

          {/* Cobertura dos sensores */}
          <div className="min-h-20 rounded-lg border border-border bg-card px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase leading-tight tracking-[0.08em] text-foreground/70">Cobertura dos sensores</p>
            <p className="mt-1.5 text-sm font-semibold text-foreground">{snapshot.waterQuality.coverage}</p>
            <p className="mt-1 text-[11px] text-foreground/75">Mínimo exigido: 3 sensores</p>
          </div>

          {/* Última leitura válida */}
          {snapshot.lastTimestamp ? (
            <div className="min-h-20 rounded-lg border border-border bg-card px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase leading-tight tracking-[0.08em] text-foreground/70">Última leitura válida</p>
              <p className="mt-1.5 text-sm font-semibold text-foreground">{new Date(snapshot.lastTimestamp).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
          ) : null}

          {/* Comunicação da unidade */}
          {sensorSource !== 'none' || loadingSource ? (
            <div className={`min-h-20 rounded-lg border px-3 py-2.5 ${sensorSource === 'aql-sensor-readings' ? 'border-emerald-200/80 bg-emerald-50/50 dark:border-emerald-500/25 dark:bg-emerald-500/10' : 'border-sky-200/80 bg-sky-50/50 dark:border-sky-500/25 dark:bg-sky-500/10'}`}>
              <p className="text-[10px] font-semibold uppercase leading-tight tracking-[0.08em] text-foreground/70">Comunicação da unidade</p>
              <p className="mt-1.5 text-sm font-semibold text-foreground">{loadingSource ? 'A sincronizar' : 'Ativa'}</p>
              <p className="mt-1 text-[11px] text-foreground/75">{loadingSource ? 'A validar feed de sensores' : sourceInfo.label}</p>
            </div>
          ) : null}
        </div>
        </div>
      ) : null}

      {visibleSensorCards.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {visibleSensorCards.map((card) => {
          const stateColor = card.state.state === 'sem_leitura' 
            ? 'border-border bg-muted/30 text-muted-foreground'
            : card.state.state === 'desatualizado'
              ? 'border-amber-200/80 bg-amber-50/50 dark:border-amber-500/30 dark:bg-amber-500/10 text-amber-700 dark:text-amber-200'
              : card.state.state === 'normal'
                ? 'border-emerald-200/80 bg-emerald-50/50 dark:border-emerald-500/25 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-200'
                : card.state.state === 'atencao'
                  ? 'border-amber-200/80 bg-amber-50/50 dark:border-amber-500/30 dark:bg-amber-500/10 text-amber-700 dark:text-amber-200'
                  : 'border-red-200/80 bg-red-50/50 dark:border-red-500/30 dark:bg-red-500/10 text-red-700 dark:text-red-200';

          return (
            <div key={card.id} className={`rounded-xl border p-3.5 shadow-sm ring-1 ring-border/45 lg:p-4 ${card.tone}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.09em] text-foreground/70">{card.title}</p>
                  <p className={`mt-1.5 flex items-baseline gap-1.5 text-[30px] font-semibold leading-none tracking-tight ${card.value === null ? 'text-foreground/50' : card.valueTone}`}>{format(card.value, card.d)}{card.unit ? <span className="text-sm font-medium text-foreground/70">{card.unit}</span> : null}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap ${stateColor}`}>{card.state.label}</span>
                  <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full border ${card.iconTone}`}><card.Icon className="h-3.5 w-3.5" /></span>
                </div>
              </div>
              <p className="mt-2 text-[11px] font-semibold text-foreground/90">{card.target}</p>
              <p className="mt-1 text-[11px] text-foreground/70">{card.text}</p>
            </div>
          );
        })}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 min-[1800px]:grid-cols-12 min-[1800px]:items-stretch">
        <div className="order-1 rounded-xl border border-border bg-gradient-to-br from-card via-card to-muted/30 p-4 shadow-sm min-[1800px]:col-span-8 min-[1800px]:col-start-1 min-[1800px]:row-start-1 dark:to-[#18263a]/65">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/70 pb-3">
            <div>
              <h3 className="flex items-center gap-2 text-[17px] font-semibold tracking-tight text-foreground"><Activity className="h-4 w-4 text-foreground" />Tendência Ambiental</h3>
              <p className="mt-1 text-xs text-foreground/75">Série temporal cruzada de temperatura, pH, O2 e salinidade com leitura contínua.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {(['24h', '7d', '30d'] as const).map((range) => (
                <button key={range} onClick={() => setTrendRange(range)} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${trendRange === range ? 'border-primary bg-primary text-primary-foreground shadow-sm' : 'border-border bg-card text-foreground hover:bg-muted/45'}`}>{range}</button>
              ))}
            </div>
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-[11px]">
            <span className="inline-flex items-center gap-1 rounded-full border border-orange-200/70 bg-orange-50/80 px-2 py-0.5 font-semibold text-orange-700 dark:border-orange-500/30 dark:bg-orange-500/12 dark:text-orange-200"><span className="h-1.5 w-3 rounded-full bg-orange-500" /> Temp</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-sky-200/70 bg-sky-50/80 px-2 py-0.5 font-semibold text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/12 dark:text-sky-200"><span className="h-1.5 w-3 rounded-full bg-sky-500" /> pH</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/70 bg-emerald-50/80 px-2 py-0.5 font-semibold text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/12 dark:text-emerald-200"><span className="h-1.5 w-3 rounded-full bg-emerald-500" /> O2</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-blue-200/70 bg-blue-50/80 px-2 py-0.5 font-semibold text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/12 dark:text-blue-200"><span className="h-1.5 w-3 rounded-full bg-blue-500" /> Salinidade</span>
            <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-semibold text-foreground/70">Temp média: {format(trendOverview.tempAvg, 1)} C</span>
            <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-semibold text-foreground/70">pH médio: {format(trendOverview.phAvg, 2)}</span>
            <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-semibold text-foreground/70">Pontos reais visíveis</span>
            <span className="ml-auto inline-flex items-center rounded-full border border-border bg-muted/35 px-2 py-0.5 text-[10px] font-semibold text-foreground/80">Pontos: {trendData.length}</span>
            {peakTemp ? <span className="inline-flex items-center rounded-full border border-orange-200/70 bg-orange-50/80 px-2 py-0.5 text-[10px] font-semibold text-orange-700 dark:border-orange-500/30 dark:bg-orange-500/12 dark:text-orange-200">Pico temp: {peakTemp.value.toFixed(1)} C</span> : null}
            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${trendOverview.outOfBand ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/12 dark:text-amber-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/12 dark:text-emerald-200'}`}>
              {trendOverview.outOfBand ? `${trendOverview.outOfBand} leituras fora da faixa` : 'Sem desvios no período'}
            </span>
          </div>
          <div className="mt-3 h-[250px] w-full md:h-[265px] min-[1800px]:h-[278px]">
            {trendData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 4" stroke={chartTone.grid} strokeOpacity={0.55} />
                  <XAxis dataKey="label" stroke={chartTone.axis} fontSize={11} tickLine={false} axisLine={false} minTickGap={22} tickMargin={8} />
                  <YAxis yAxisId="left" stroke={chartTone.axis} fontSize={11} tickLine={false} axisLine={false} width={42} tickMargin={8} />
                  <YAxis yAxisId="right" orientation="right" domain={[4.5, 9.2]} stroke={chartTone.axis} fontSize={11} tickLine={false} axisLine={false} width={36} tickMargin={6} />
                  <Tooltip
                    cursor={{ stroke: chartTone.cursor, strokeDasharray: '3 3' }}
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const row = payload[0].payload;
                      const status =
                        (typeof row.temperature === 'number' && metricLevel('temperature', row.temperature) === 'Critico')
                        || (typeof row.oxygen === 'number' && metricLevel('oxygen', row.oxygen) === 'Critico')
                        || (typeof row.ph === 'number' && metricLevel('ph', row.ph) === 'Critico')
                          ? { label: 'Crítico', tone: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/12 dark:text-red-200' }
                          : (typeof row.temperature === 'number' && metricLevel('temperature', row.temperature) === 'Warning')
                            || (typeof row.oxygen === 'number' && metricLevel('oxygen', row.oxygen) === 'Warning')
                            || (typeof row.ph === 'number' && metricLevel('ph', row.ph) === 'Warning')
                              ? { label: 'Atenção', tone: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/12 dark:text-amber-200' }
                              : { label: 'OK', tone: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/12 dark:text-emerald-200' };
                      return (
                        <div className="min-w-[240px] rounded-lg border border-border bg-card p-3 shadow-lg">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-foreground">{new Date(row.timestamp).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${status.tone}`}>{status.label}</span>
                          </div>
                          <div className="mt-2 space-y-1 text-xs">
                            <p className="flex items-center justify-between gap-2 text-foreground"><span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-orange-500" /> Temperatura</span><span className="font-semibold text-foreground">{format(row.temperature, 1)} C</span></p>
                            <p className="flex items-center justify-between gap-2 text-foreground"><span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-sky-500" /> pH</span><span className="font-semibold text-foreground">{format(row.ph, 2)}</span></p>
                            <p className="flex items-center justify-between gap-2 text-foreground"><span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> O2</span><span className="font-semibold text-foreground">{format(row.oxygen, 1)} mg/L</span></p>
                            <p className="flex items-center justify-between gap-2 text-foreground"><span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-500" /> Salinidade</span><span className="font-semibold text-foreground">{format(row.salinity, 1)} PSU</span></p>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <ReferenceLine yAxisId="left" y={30} stroke={chartTone.temp} strokeDasharray="4 5" strokeOpacity={0.55} />
                  <ReferenceLine yAxisId="left" y={17} stroke={chartTone.temp} strokeDasharray="4 5" strokeOpacity={0.5} />
                  <ReferenceLine yAxisId="right" y={5} stroke={chartTone.oxygen} strokeDasharray="4 5" strokeOpacity={0.5} />
                  <Line yAxisId="left" type="monotone" dataKey="temperature" stroke={chartTone.temp} strokeWidth={2.7} dot={temperatureDot} activeDot={{ r: 4.5, strokeWidth: 2, stroke: 'var(--card)' }} connectNulls />
                  <Line yAxisId="right" type="monotone" dataKey="ph" stroke={chartTone.ph} strokeWidth={2.4} dot={rawPointDot(chartTone.ph)} activeDot={{ r: 3.8, strokeWidth: 2, stroke: 'var(--card)' }} connectNulls />
                  <Line yAxisId="right" type="monotone" dataKey="oxygen" stroke={chartTone.oxygen} strokeWidth={2.4} dot={rawPointDot(chartTone.oxygen)} activeDot={{ r: 3.8, strokeWidth: 2, stroke: 'var(--card)' }} connectNulls />
                  <Line yAxisId="left" type="monotone" dataKey="salinity" stroke={chartTone.salinity} strokeWidth={2.4} dot={rawPointDot(chartTone.salinity)} activeDot={{ r: 3.8, strokeWidth: 2, stroke: 'var(--card)' }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 px-6 text-center">
                <div className="max-w-md space-y-2">
                  <p className="text-sm font-semibold text-foreground">Histórico Temporal</p>
                  <p className="text-xs text-muted-foreground">Ainda não existem leituras suficientes para calcular tendência. Com mais amostras, será apresentada a evolução temporal e a comparação com a faixa ideal.</p>
                  <button type="button" onClick={onOpenAlerts} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted/40">
                    Ver alertas
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="order-2 rounded-xl border border-border bg-gradient-to-br from-card via-card to-muted/30 p-4 shadow-sm min-[1800px]:col-span-8 min-[1800px]:col-start-1 min-[1800px]:row-start-2 dark:to-[#18263a]/60">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-[17px] font-semibold tracking-tight text-foreground">Distribuição por Localização / Tanque / Linha</h3>
              <p className="mt-1 text-xs text-foreground/75">Box plots compactos com pontos reais, mediana, quartis e outliers por métrica ambiental.</p>
            </div>
            <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-semibold text-foreground/70">{distributionRows.length} distribuições</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
            {visibleDistributionMetrics.map((metric) => (
              <span key={metric.key} className={`inline-flex items-center gap-1 rounded-full border bg-card px-2 py-0.5 font-semibold ${metric.tone}`}>
                <span className="h-1.5 w-3 rounded-full" style={{ backgroundColor: metric.stroke }} />
                {metric.label}
              </span>
            ))}
            <span className="ml-auto inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-semibold text-foreground/70">Escala real por métrica</span>
          </div>
          <div className="mt-3 max-h-[282px] overflow-auto pr-1 min-[1800px]:max-h-[320px]">
            {distributionRows.length ? (
              <div className="min-w-[720px] space-y-2">
                <div className="grid grid-cols-[minmax(6.5rem,8rem)_minmax(4.25rem,5rem)_minmax(13rem,1fr)_minmax(6.5rem,7.5rem)] gap-3 px-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  <span>Tanque</span>
                  <span>Métrica</span>
                  <span>Distribuição real</span>
                  <span className="text-right">Mediana</span>
                </div>
                {distributionRows.map((row) => {
                  const domain = distributionDomains.get(row.metric);
                  return domain ? <DistributionBoxPlot key={row.id} row={row} domain={domain} /> : null;
                })}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 px-6 text-center">
                <p className="max-w-md text-xs text-muted-foreground">Sem leituras suficientes para calcular distribuição por localização, tanque ou linha.</p>
              </div>
            )}
          </div>
        </div>

        <div className="order-3 rounded-xl border border-border bg-gradient-to-br from-card via-card to-muted/30 p-4 shadow-sm min-[1800px]:col-span-4 min-[1800px]:col-start-9 min-[1800px]:row-start-1 min-[1800px]:row-span-2 min-[1800px]:flex min-[1800px]:h-full min-[1800px]:flex-col dark:to-[#18263a]/55">
          <div className="rounded-lg border border-border bg-muted/35 px-3 py-2.5">
            <h3 className="text-[17px] font-semibold tracking-tight text-foreground">Alertas por Threshold</h3>
            <p className="mt-1 text-xs text-foreground/75">Estado por métrica com threshold, valor atual e recomendação operacional.</p>
          </div>
          <div className="mt-2.5 grid grid-cols-3 gap-2 text-[11px]">
            <div className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-red-700 dark:border-red-500/35 dark:bg-red-500/12 dark:text-red-200"><p className="font-semibold">Crítico</p><p className="text-[10px]">{thresholdSummary.critical} métrica(s)</p></div>
            <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-amber-700 dark:border-amber-500/35 dark:bg-amber-500/12 dark:text-amber-200"><p className="font-semibold">Atenção</p><p className="text-[10px]">{thresholdSummary.warning} métrica(s)</p></div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200"><p className="font-semibold">OK</p><p className="text-[10px]">{thresholdSummary.normal} métrica(s)</p></div>
          </div>
          <div className="mt-2.5 space-y-2 min-[1800px]:flex-1 min-[1800px]:overflow-y-auto min-[1800px]:pr-1">
            {thresholds.map((item) => (
              <div key={item.metric} className="rounded-lg border border-border bg-card/95 px-3 py-2.5 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold text-foreground">{item.metric}</p>
                    <p className="mt-0.5 text-[11px] text-foreground/70">{item.hint}</p>
                  </div>
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${METRIC_TONE[item.level]}`}>{METRIC_LABEL[item.level]}</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                  <div className="rounded-md border border-border bg-muted/35 px-2 py-1.5">
                    <p className="font-medium uppercase tracking-[0.06em] text-foreground/65">Threshold</p>
                    <p className="mt-0.5 font-semibold text-foreground">{item.threshold}</p>
                  </div>
                  <div className="rounded-md border border-border bg-muted/35 px-2 py-1.5">
                    <p className="font-medium uppercase tracking-[0.06em] text-foreground/65">Valor atual</p>
                    <p className="mt-0.5 font-semibold text-foreground">{item.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2.5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-500/30 dark:bg-amber-500/10">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-700 dark:text-amber-200" />
            <p className="text-[11px] text-amber-800 dark:text-amber-100">Ative escalonamento automático por localização para reduzir tempo de resposta operacional.</p>
          </div>
          <button onClick={onOpenAlerts} className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted/45">
            Ver alertas relacionados
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
